import React from 'react';
import { Button, Space } from 'antd';
import { SaveOutlined, HomeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import commonSelector from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { PAGE_TYPE } from '@utils/constants';
import { changeSaveDashboardPromptVisibility } from '@store/dashboards/ui/actions';
import * as dashboardsUIActions from '@store/dashboards/ui/actions';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useDefaultLandingPageMutation } from '@hooks/useDefaultLandingPage';
import { onSetDefaultLanding } from '@components/common/kebabMenuUtils/defaultLandingPage';
import { Header } from './styled';

interface ActionsProps {
  currentShareId: string;
  pageType: number;
}

const Actions: React.FC<ActionsProps> = ({ currentShareId, pageType }) => {
  const dispatch = useDispatch();
  const { features, profiles } = useSelector(commonSelector);

  // Save feature
  const isOverviewSaveEnabled = features[featureNames.OVB_SAVE];
  const showSaveOption = isOverviewSaveEnabled;

  // Delete feature - only for saved dashboards
  const showDeleteOption = pageType === PAGE_TYPE.SAVED_DASHBOARD;

  // Make default feature
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    path: state.path,
    name: state.name,
  }));

  const { setDefaultLandingPage: mutateLandingPage } = useDefaultLandingPageMutation();

  const { pathname } = window.location;
  const defaultPathname = defaultLandingPage.path || '';
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);
  const cid = profiles.filter((p) => p.is_selected)[0]?.customer_id;
  const setLandingPageEnabled =
    (features[featureNames.DEFAULT_LANDING_PAGE] || true) && pageType !== PAGE_TYPE.SHARING;

  const handleSaveClick = () => {
    if (pageType === PAGE_TYPE.SHARING) {
      dispatch(changeSaveDashboardPromptVisibility(true, currentShareId));
    } else {
      dispatch(dashboardsUIActions.toggleUpdatePopupVisible(true, currentShareId));
    }
  };

  // Determine if save button should be enabled
  const enableSave = pageType === PAGE_TYPE.SHARING || pageType === PAGE_TYPE.SAVED_DASHBOARD;

  const handleMakeDefaultClick = () => {
    onSetDefaultLanding(dispatch, defaultLandingPage, cid, mutateLandingPage);
  };

  const handleDeleteClick = () => {
    dispatch(dashboardsUIActions.toggleRemovePopupVisible());
  };

  // Check if we should show the Actions section at all
  const shouldShowActions = showSaveOption || setLandingPageEnabled || showDeleteOption;

  if (!shouldShowActions) {
    return null;
  }

  return (
    <Space direction="vertical">
      <Header>Actions</Header>
      {showSaveOption && (
        <Button
          onClick={handleSaveClick}
          type="text"
          size="small"
          icon={<SaveOutlined />}
          disabled={!enableSave}
        >
          Save
        </Button>
      )}
      {setLandingPageEnabled && (
        <Button
          onClick={handleMakeDefaultClick}
          type="text"
          size="small"
          icon={<HomeOutlined />}
          disabled={isDefaultLandingPage}
        >
          Set as landing page
        </Button>
      )}
      {showDeleteOption && (
        <Button
          onClick={handleDeleteClick}
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          danger
        >
          Delete
        </Button>
      )}
    </Space>
  );
};

export default Actions;
